import type { Panorama, ProviderId } from '../providers/types';
import { haversineKm, isValidLatLng, type LatLng } from './geo';
import { getGameMap } from './maps';
import { ROUNDS_PER_GAME, scoreForDistance } from './scoring';
import type { GameSettings } from './settings';
import type { GameRecord } from './stats';

export type Phase = 'home' | 'loading' | 'playing' | 'result' | 'summary' | 'error';

export interface Round {
  panorama: Panorama;
  guess: LatLng | null;
  distanceKm: number | null;
  score: number;
  timedOut: boolean;
}

export interface GameError {
  message: string;
  /** True when retrying the same source makes sense. */
  retryable: boolean;
}

export interface GameState {
  phase: Phase;
  /** Incremented at every new game, used to cancel stale async work. */
  gameId: number;
  settings: GameSettings;
  /** Source currently used (can differ from settings after an automatic fallback). */
  providerId: ProviderId;
  rounds: Round[];
  /** Index of the current round (0-based). */
  roundIndex: number;
  /** Marker placed on the map but not validated yet. */
  pendingGuess: LatLng | null;
  error: GameError | null;
  notice: string | null;
  outcome: { isBestOverall: boolean; isBestOnMap: boolean } | null;
}

export type GameAction =
  | { type: 'start'; settings: GameSettings }
  | { type: 'round-loaded'; panorama: Panorama }
  | { type: 'load-failed'; error: GameError }
  | { type: 'provider-switched'; providerId: ProviderId; notice: string }
  | { type: 'reload-round'; notice?: string }
  | { type: 'retry' }
  | { type: 'set-guess'; guess: LatLng }
  | { type: 'submit'; timedOut?: boolean }
  | { type: 'next' }
  | { type: 'game-recorded'; outcome: { isBestOverall: boolean; isBestOnMap: boolean } }
  | { type: 'dismiss-notice' }
  | { type: 'home' };

export function initialGameState(settings: GameSettings): GameState {
  return {
    phase: 'home',
    gameId: 0,
    settings,
    providerId: settings.providerId,
    rounds: [],
    roundIndex: 0,
    pendingGuess: null,
    error: null,
    notice: null,
    outcome: null,
  };
}

export function evaluateGuess(
  actual: LatLng,
  guess: LatLng | null,
  scaleKm: number,
): { distanceKm: number | null; score: number } {
  if (!guess) return { distanceKm: null, score: 0 };
  const distanceKm = haversineKm(actual, guess);
  return { distanceKm, score: scoreForDistance(distanceKm, scaleKm) };
}

export function totalScore(rounds: Round[]): number {
  return rounds.reduce((sum, r) => sum + r.score, 0);
}

export function gameReducer(state: GameState, action: GameAction): GameState {
  switch (action.type) {
    case 'start':
      return {
        ...initialGameState(action.settings),
        phase: 'loading',
        gameId: state.gameId + 1,
      };

    case 'round-loaded': {
      if (state.phase !== 'loading') return state;
      const round: Round = { panorama: action.panorama, guess: null, distanceKm: null, score: 0, timedOut: false };
      const rounds = [...state.rounds.slice(0, state.roundIndex), round];
      return { ...state, phase: 'playing', rounds, pendingGuess: null, error: null };
    }

    case 'load-failed':
      if (state.phase !== 'loading') return state;
      return { ...state, phase: 'error', error: action.error };

    case 'provider-switched':
      return { ...state, providerId: action.providerId, notice: action.notice };

    case 'reload-round':
      // The current panorama could not be displayed: fetch another location for the same round.
      if (state.phase !== 'playing') return state;
      return {
        ...state,
        phase: 'loading',
        rounds: state.rounds.slice(0, state.roundIndex),
        pendingGuess: null,
        notice: action.notice ?? state.notice,
      };

    case 'retry':
      if (state.phase !== 'error') return state;
      return { ...state, phase: 'loading', error: null };

    case 'set-guess':
      if (state.phase !== 'playing' || !isValidLatLng(action.guess)) return state;
      return { ...state, pendingGuess: action.guess };

    case 'submit': {
      if (state.phase !== 'playing') return state;
      const round = state.rounds[state.roundIndex];
      if (!round) return state;
      // Without a marker the guess can only be submitted when the time is up.
      if (!state.pendingGuess && !action.timedOut) return state;
      const { distanceKm, score } = evaluateGuess(
        round.panorama.position,
        state.pendingGuess,
        getGameMap(state.settings.mapId).scaleKm,
      );
      const rounds = [...state.rounds];
      rounds[state.roundIndex] = { ...round, guess: state.pendingGuess, distanceKm, score, timedOut: !!action.timedOut };
      return { ...state, phase: 'result', rounds };
    }

    case 'next':
      if (state.phase !== 'result') return state;
      if (state.roundIndex + 1 >= ROUNDS_PER_GAME) return { ...state, phase: 'summary' };
      return { ...state, phase: 'loading', roundIndex: state.roundIndex + 1, pendingGuess: null };

    case 'game-recorded':
      return { ...state, outcome: action.outcome };

    case 'dismiss-notice':
      return { ...state, notice: null };

    case 'home':
      return { ...initialGameState(state.settings), gameId: state.gameId + 1 };

    default:
      return state;
  }
}

/** Builds the record saved in the statistics once the game is over. */
export function toGameRecord(state: GameState, id: string, date: Date = new Date()): GameRecord {
  return {
    id,
    date: date.toISOString(),
    mapId: state.settings.mapId,
    providerId: state.providerId,
    score: totalScore(state.rounds),
    timeLimit: state.settings.timeLimit,
    allowMove: state.settings.allowMove,
    rounds: state.rounds.map((r) => ({ score: r.score, distanceKm: r.distanceKm })),
  };
}
